import React, { useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print';
import PrintComponent from '../components/PrintComponent';

const OrderItem = ({ order, orders, setOrders, menuSections, onDelete }) => {
	const [editedItems, setEditedItems] = useState([]);
	const [editingOrder, setEditingOrder] = useState(null);

	// Format time display
	const formatTime = (date) => {
		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true
		});
	};

	// Enter edit mode
	const startEditing = (order) => {
		setEditingOrder(order.id);
		setEditedItems([...order.items]);
	};

	// Cancel editing
	const cancelEditing = () => {
		setEditingOrder(null);
		setEditedItems([]);
	};

	// Save edited order
	const saveEditedOrder = () => {
		setOrders(orders.map(order => {
			if (order.id === editingOrder) {
				return {
					...order,
					items: editedItems,
					total: editedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)
				};
			}
			return order;
		}));
		setEditingOrder(null);
		setEditedItems([]);
	};

	// Modify item quantity in edited order
	const updateItemQuantity = (index, newQuantity) => {
		const updatedItems = [...editedItems];
		if (newQuantity > 0) {
			updatedItems[index].quantity = Number(newQuantity);
		} else {
			updatedItems.splice(index, 1);
		}
		setEditedItems(updatedItems);
	};

	// Add item to edited order
	const addItemToEdit = (sectionIndex, itemIndex, quantity) => {
		const item = menuSections[sectionIndex]?.items[itemIndex];
		if (!item) return;

		const existingItem = editedItems.find(i => i.name === item.name);
		const newItems = existingItem
			? editedItems.map(i =>
				i.name === item.name
					? { ...i, quantity: i.quantity + quantity }
					: i
			)
			: [...editedItems, {
				...item,
				quantity,
				price: parseFloat(item.price.replace('$', ''))
			}];

		setEditedItems(newItems);
	};

	const printRef = useRef();
	const handlePrint = useReactToPrint({
		content: () => printRef.current,
		contentRef: printRef,
	});

	return (
		<div key={order.id} className="bg-white p-5 rounded-lg shadow-md">
			<div className="flex justify-between items-center mb-3">
				<span className="font-bold text-lg">Order #{order.id}</span>
			</div>
			<p className="text-gray-500 text-sm mb-3">
				Ordered: {formatTime(order.orderedAt)}
			</p>

			{/* Items List - Editable when in edit mode */}
			<div className="border-t border-b py-3 my-3">
				{editingOrder === order.id ? (
					// Edit mode items
					editedItems.map((item, index) => (
						<div key={index} className="flex items-center gap-2 mb-2">
							<span className="flex-1">{item.quantity}x {item.name}</span>
							<input
								type="number"
								min="0"
								value={item.quantity}
								onChange={(e) => updateItemQuantity(index, e.target.value)}
								className="w-20 p-1 border rounded"
							/>
						</div>
					))
				) : (
					// Normal display items
					order.items.map((item, index) => (
						<div key={index} className="flex justify-between mb-1">
							<span>{item.quantity}x {item.name}</span>
							<span>${(item.price * item.quantity).toFixed(2)}</span>
						</div>
					))
				)}
			</div>

			{/* Add Item dropdown in edit mode */}
			{editingOrder === order.id && (
				<div className="mb-4">
					<select
						className="w-full p-2 border rounded mb-2"
						onChange={(e) => {
							const [sectionIndex, itemIndex] = e.target.value.split('-').map(Number);
							addItemToEdit(sectionIndex, itemIndex, 1);
							e.target.value = '';
						}}
					>
						<option value="">Add Menu Item</option>
						{menuSections.map((section, sectionIndex) => (
							<optgroup key={sectionIndex} label={section.title}>
								{section.items.map((item, itemIndex) => (
									<option
										key={`${sectionIndex}-${itemIndex}`}
										value={`${sectionIndex}-${itemIndex}`}
									>
										{item.name} - {item.price}
									</option>
								))}
							</optgroup>
						))}
					</select>
				</div>
			)}

			{/* Total */}
			<div className="flex justify-between font-bold text-lg">
				<span>Total</span>
				<span>${editingOrder === order.id ?
					editedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2) :
					order.total}
				</span>
			</div>

			{/* Action Buttons */}
			{editingOrder === order.id ? (
				// Edit mode buttons
				<div className="mt-4 flex gap-3">
					<button
						onClick={saveEditedOrder}
						className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
					>
						Save
					</button>
					<button
						onClick={cancelEditing}
						className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
					>
						Cancel
					</button>
				</div>
			) : (
				// Normal mode buttons - only print and edit buttons
				<div className="mt-4 flex gap-3 flex-wrap">
					<button
						className="flex-1 px-4 py-2 bg-gold-dark text-white rounded hover:bg-gold"
						onClick={handlePrint}
					>
						Print
					</button>
					<button
						className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
						onClick={() => startEditing(order)}
					>
						Edit
					</button>
					<button
						className="flex-1 px-4 py-2 border text-red-600 font-semibold border-gray-300 rounded hover:bg-gray-100"
						onClick={() => onDelete()}
					>
						Delete
					</button>
				</div>
			)}
			<div className='sr-only'>
				<PrintComponent
					ref={printRef}
					order={order}
					isEditing={editingOrder === order.id}
					editedItems={editedItems}
				/>
			</div>
		</div>
	)
}

export default OrderItem